import type { MultiLangField } from "helpers/functions";
import type { IMembers, IResearch, IResearchByAlias } from "entities/market";

// Research писался в старой админке, а теперь ведётся в Strapi. Старые записи
// остаются, новые добавляются к ним, поэтому запись из Strapi приводится к той
// же форме, что отдаёт старый бэкенд, и экраны не нужно переучивать.

interface StrapiAuthor {
  id?: number;
  name?: string | null;
  role?: string | null;
  image?: string | null;
}

export interface StrapiResearchItem {
  id: number;
  isActive?: boolean;
  title?: string | null;
  subtitle?: string | null;
  category?: string | null;
  date?: string | null;
  alias: string;
  image?: string | null;
  author?: StrapiAuthor | null;
  pdf?: string | null;
}

// Research ведётся только на английском, поэтому одно значение заполняет все
// языки: getMLValue ждёт объект, а на строке он вернёт первый символ.
const ml = (value?: string | null): MultiLangField => {
  const text = value || "";
  return { en: text, ru: text, am: text };
};

// Старые записи хранят "YYYY.MM.DD"; и сортировка списка, и moment в UI
// завязаны на этот формат, а Strapi отдаёт ISO "YYYY-MM-DD".
const toLegacyDate = (date?: string | null) => (date || "").replace(/-/g, ".");

// У всех записей старой админки одна и та же обложка. Своей картинки в Strapi
// нет, поэтому берём её же — список остаётся визуально единым.
const DEFAULT_COVER = "photo_5391270025316393374_y.jpg";

const toAuthors = (author?: StrapiAuthor | null): IMembers[] => {
  if (!author || !author.name) return [];
  return [
    {
      id: author.id ?? 0,
      name: ml(author.name),
      role: ml(author.role),
      image: author.image || "",
    },
  ];
};

// authors остаётся пустым: старый список сопоставляет его с id из /members,
// а автор из Strapi приходит уже внутри записи и едет в strapiAuthors.
export const toLegacyResearch = (item: StrapiResearchItem): IResearch => ({
  id: item.id,
  alias: item.alias,
  title: ml(item.title),
  subtitle: ml(item.subtitle),
  date: toLegacyDate(item.date),
  background_image: item.image || DEFAULT_COVER,
  categories: [],
  authors: [],
  strapiAuthors: toAuthors(item.author),
  isStrapi: true,
});

export const toLegacyResearchByAlias = (
  item: StrapiResearchItem,
): IResearchByAlias => ({
  ...toLegacyResearch(item),
  pdf: ml(item.pdf),
});

export const normalizeStrapiResearchList = (
  raw: unknown,
): IResearch[] => {
  const items = Array.isArray(raw) ? (raw as StrapiResearchItem[]) : [];
  const seen = new Set<string>();
  return items
    .filter((i) => i.isActive !== false)
    .filter((i) => Boolean(i.alias) && !seen.has(i.alias) && seen.add(i.alias))
    .map(toLegacyResearch);
};
